import React, { useState, useEffect } from "react";
import { Row, Col, Button, Container, FormGroup } from "reactstrap";
import { connect } from "react-redux";
import logo from "../assets/images/logo-main.png";
import { Link, withRouter } from "react-router-dom";
import {
  checkLogin,
  apiError,
  clearAllLoginMessages,
  verifyTotpRequest,
} from "../store/actions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import spinner from "../assets/images/loadingGif.gif";
import { Snackbar } from "@mui/material";
import { Alert } from "@mui/material";
import QrCode from "./Authentication/QrCode";
import TotpCodeInput from "./Authentication/TotpCodeInput";

const Login = (props) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Login, 2: MFA Setup, 3: OTP Verification
  const [base64Image, setBase64Image] = useState(null);
  const [session, setSession] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    props.checkLogin(
      { userName: username, password: password },
      props.history
    );
  };

  const handleVerifyTotp = (code) => {
    props.verifyTotpRequest(session, code);
  };

  const handleLoginWithTotp = (totp) => {
    props.checkLogin(
      {
        userName: username,
        password: password,
        mfaCode: totp,
      },
      props.history
    );
  };

  const { loginError, message, clearAllLoginMessages, stage, mfaSetup } = props;

  useEffect(() => {
    if (loginError) {
      setSnackbarMessage(loginError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      clearAllLoginMessages();
    }
  }, [loginError, clearAllLoginMessages]);

  useEffect(() => {
    if (message) {
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setStep(1);
      setBase64Image(null);
      setSession(null);
      clearAllLoginMessages();
    }
  }, [message, clearAllLoginMessages]);

  useEffect(() => {
    if (
      stage === "MFASETUP" &&
      mfaSetup &&
      !base64Image &&
      step !== 2
    ) {
      setBase64Image(mfaSetup.base64Image);
      setSession(mfaSetup.session);
      setStep(2);
    }

    if (stage === "VERIFICATION" && step !== 3) {
      setStep(3);
    }
  }, [stage, mfaSetup, base64Image, step]);

  return (
    <React.Fragment>
      <Container fluid className="p-0">
        <Row className="no-gutters">
          <Col lg={5}>
            <div className="authentication-page-content p-4 d-flex align-items-center min-vh-100">
              <div className="w-100">
                <Row className="justify-content-center">
                  <Col lg={9}>
                    <div>
                      <div className="text-center">
                        <div>
                          <Link to="/" className="logo">
                            <img src={logo} height="60" alt="logo" />
                          </Link>
                        </div>
                        <p className="text-muted">CloudNova License Manager</p>
                      </div>

                      <div className="p-2 mt-5">
                        {step === 1 && (
                          <form
                            className="form-horizontal"
                            onSubmit={handleSubmit}
                          >
                            <FormGroup>
                              <TextField
                                fullWidth
                                name="username"
                                value={username}
                                label="Username"
                                onChange={(e) => setUsername(e.target.value)}
                                required
                              />
                            </FormGroup>

                            <FormGroup>
                              <TextField
                                fullWidth
                                name="password"
                                value={password}
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                      >
                                        {showPassword ? (
                                          <VisibilityOff />
                                        ) : (
                                          <Visibility />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </FormGroup>

                            <div className="mt-4 text-center">
                              <Button
                                color="primary"
                                className="w-md waves-effect waves-light"
                                type="submit"
                                disabled={props.loading}
                              >
                                Log In
                              </Button>
                            </div>

                            <div className="mt-4 text-center">
                              <Link to="/forgot-password" className="text-muted">
                                <i className="mdi mdi-lock mr-1"></i> Forgot your
                                password?
                              </Link>
                            </div>
                          </form>
                        )}

                        {step === 2 && (
                          <div className="text-center">
                            <h5 className="mb-3">MFA Set Up</h5>
                            <p className="text-muted">
                              Scan the QR code with your authenticator app, then
                              enter the 6-digit code. After pairing succeeds, wait
                              for the code to refresh before using it to log in.
                            </p>
                            <QrCode base64Image={base64Image} />
                            <TotpCodeInput
                              onSubmit={handleVerifyTotp}
                              loading={props.loading}
                              submitLabel="Confirm MFA"
                            />
                          </div>
                        )}

                        {step === 3 && (
                          <div className="text-center">
                            <h5 className="mb-3">MFA Verification</h5>
                            <p className="text-muted">
                              Enter the 6-digit code from your authenticator app.
                            </p>
                            <TotpCodeInput
                              onSubmit={handleLoginWithTotp}
                              loading={props.loading}
                              submitLabel="Verify"
                            />
                          </div>
                        )}

                        {props.loading && (
                          <div
                            style={{
                              margin: "20px",
                              display: "flex",
                              direction: "row",
                              alignContent: "center",
                              alignItems: "center",
                              justifyContent: "center",
                              justifyItems: "center",
                            }}
                          >
                            <img src={spinner} width="80px" alt="Spinner" />
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={handleSnackbarClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                >
                  <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                  >
                    {snackbarMessage}
                  </Alert>
                </Snackbar>
              </div>
            </div>
          </Col>
          <Col lg={7}>
            <div className="authentication-bg">
              {/* bg-overlay */}
              <div className=""></div>
            </div>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

const mapStatetoProps = (state) => {
  const { loginError, loading, stage, mfaSetup, message } = state.login;
  return { loginError, loading, stage, mfaSetup, message };
};

export default withRouter(
  connect(mapStatetoProps, {
    checkLogin,
    apiError,
    clearAllLoginMessages,
    verifyTotpRequest,
  })(Login)
);
